import React from 'react';
import { AiFillStar, AiOutlineStar } from 'react-icons/ai';
import classNames from 'styles/utils/classNames';
import { formatMarketCap } from 'screens/Calendar';
import { annualizeYield } from '../../columns/dividendsColumns';
import { TickerType } from '../../types';

interface DividendsCardProps {
  item: any;
  isBookmarked: boolean;
  onBookmark: (symbol: string) => void;
  tickerData: TickerType[];
}

export const DividendsCard: React.FC<DividendsCardProps> = ({ item, isBookmarked, onBookmark, tickerData }) => {
  const foundTicker = tickerData.find((ticker) => ticker.t === item.symbol);
  const annualYieldValue = annualizeYield(item.yield, item.frequency);

  return (
    <div className="rounded-lg border border-palette-border bg-palette-black-2 p-2.5">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-start gap-2">
          <button
            onClick={() => onBookmark(item.symbol)}
            className={classNames(
              'group p-1 transition-all duration-200',
              isBookmarked
                ? 'text-yellow-400'
                : 'text-gray-400 hover:text-yellow-400'
            )}
            aria-label={`${isBookmarked ? 'Remove' : 'Add'} ${item.symbol} bookmark`}
          >
            {isBookmarked ? (
              <AiFillStar className="h-4 w-4" />
            ) : (
              <>
                <AiOutlineStar className="h-4 w-4 group-hover:hidden" />
                <AiFillStar className="hidden h-4 w-4 group-hover:block" />
              </>
            )}
          </button>
          <img
            src={`https://images.financialmodelingprep.com/symbol/${item.symbol}.png`}
            alt={item.symbol}
            className="h-8 w-8 rounded-lg"
          />
          <div>
            <div className="text-white text-xs font-semibold">
              {item.symbol}
            </div>
            {foundTicker?.n && (
              <div className="text-gray-400 text-xs">
                {foundTicker?.n || item.symbol}
              </div>
            )}
            {item.marketCap && (
              <div className="text-gray-400 text-xs">
                {formatMarketCap(item.marketCap)} Market Cap
              </div>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-gray-400 text-xs">Yield</div>
          <div className="text-white text-xs">
            {(typeof item.yield === 'number' && !isNaN(item.yield))
              ? `${item.yield.toFixed(2)}%` : '--'} (Quarterly)
          </div>
          <div className="text-gray-400 text-xs">{annualYieldValue} (Annual)</div>
        </div>
      </div>
      <div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <span className="block text-gray-400">Adj. Dividend</span>
            <span>{
              typeof item.adjDividend === 'number'
                ? item.adjDividend.toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 4,
                })
                : '--'
            }</span>
          </div>
          <div className="text-right">
            <span className="block text-gray-400 text-xs">Dividend</span>
            <span>{typeof item.dividend === 'number'
              ? item.dividend.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 4,
              })
              : '--'}</span>
          </div>
        </div>
      </div>
      <div className="py-1">
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="block text-gray-400 text-xs">Declaration Date</span>
            <span>{item.declarationDate || '--'}</span>
          </div>
          <div className="text-right">
            <span className="block text-gray-400 text-xs">Ex-Dividend Date</span>
            <span className='text-xs'>{item.date || '--'}</span>
          </div>
        </div>
      </div>
      <div className="pt-1">
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="block text-gray-400 text-xs">Record Date</span>
            <span className='text-xs'>{item.recordDate || '--'}</span>
          </div>
          <div className="text-right">
            <span className="block text-gray-400 text-xs">Payment Date</span>
            <span className='text-xs'>{item.paymentDate || '--'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
